import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { toast } from "sonner";

import { useCan, useRole } from "@/app/session/permissions";
import { useCurrentUser } from "@/app/session/useSession";
import { useClients } from "@/features/clients/api";
import { CLIENT_KINDS } from "@/features/clients/kinds";
import { usePageTitle } from "@/shared/hooks/useDocumentTitle";
import { useT } from "@/shared/i18n/useT";

const COUNT_PREFIX = /^\(\d+\) /;

/**
 * Tells agents and team leaders the moment leads land on them (new, rotated in, pulled from
 * the pool): a toast that opens the board, and the count in the tab title until they look. The
 * Fresh timer is short, so a lead that waits unseen in a background tab is a lost lead.
 * Renders nothing; the leads list it reads refreshes every minute (see useClients).
 */
export function NewLeadsNotice() {
  const role = useRole();
  const canLeads = useCan("lead.view");
  return canLeads && role !== "admin" ? <Watcher /> : null;
}

function Watcher() {
  const t = useT();
  const me = useCurrentUser();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const pageTitle = usePageTitle();
  const leads = useClients(CLIENT_KINDS.lead).data;
  const known = useRef<ReadonlySet<number> | null>(null);
  const [unseen, setUnseen] = useState(0);

  useEffect(() => {
    if (!leads) return;
    const mineList = leads.filter((lead) => lead.assignedToId === me.id);
    const mine = new Set(mineList.map((lead) => lead.id));
    // The first list is the baseline: what was already theirs isn't news, and neither is a lead
    // they created or took themselves.
    const before = known.current;
    const fresh = before
      ? mineList.filter((lead) => !before.has(lead.id) && lead.assignedBy?.id !== me.id).length
      : 0;
    known.current = mine;
    if (fresh === 0) return;
    setUnseen((count) => count + fresh);
    toast.info(t("clients.notice.newLeads", { count: fresh }), {
      action: {
        label: t("clients.notice.open"),
        onClick: () => {
          void navigate("/leads");
        },
      },
    });
  }, [leads, me.id, navigate, t]);

  // Opening the board means they've seen them.
  const onBoard = pathname.startsWith("/leads");
  if (onBoard && unseen > 0) setUnseen(0);

  useEffect(() => {
    const base = document.title.replace(COUNT_PREFIX, "");
    document.title = unseen > 0 ? `(${String(unseen)}) ${base}` : base;
  }, [unseen, pageTitle]);

  return null;
}
